import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Masthead } from "@/components/Masthead";
import { WeatherBar } from "@/components/WeatherBar";
import { SafetyRibbon } from "@/components/SafetyRibbon";
import { SectionLabel } from "@/components/SectionLabel";
import { LeadStory } from "@/components/LeadStory";
import { StoryCard } from "@/components/StoryCard";
import { PersonCard } from "@/components/PersonCard";
import { Poll } from "@/components/Poll";
import { QuickWonders } from "@/components/QuickWonders";
import { TrustBox } from "@/components/TrustBox";
import { Footer } from "@/components/Footer";
import { ArticleModal } from "@/components/ArticleModal";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Section layout ──
// Topic diversity is enforced ONCE, on the backend (`/articles` caps each
// topic and drops same-story repeats before responding). This file used to
// re-run its own clustering over the already-filtered list at a stricter
// 1-per-topic, which silently dropped stories the server had deliberately
// kept — and the mobile app, which does no client-side filtering, therefore
// showed a different front page from the same endpoint.
//
// It also depended on `original_content`, the raw un-rewritten wire copy. That
// field is no longer served to clients (it is the adult framing the safety
// pipeline exists to strip), so this is the only correct place for the logic
// anyway. The client's job is now purely to lay out what it is given.
function buildHomepageSections(allArticles, leadArticle) {
  const leadId = leadArticle?.id;
  const available = allArticles.filter((a) => a.id !== leadId);

  // Top row: one story from each of these categories, in this order, so the
  // three cards under the hero are visibly different kinds of news.
  const gridCategories = ["science", "tech", "sports", "world", "people"];
  const gridArticles = [];
  const usedIds = new Set(leadId ? [leadId] : []);

  for (const cat of gridCategories) {
    if (gridArticles.length >= 3) break;
    const pick = available.find((a) => a.category === cat && !usedIds.has(a.id));
    if (pick) {
      gridArticles.push(pick);
      usedIds.add(pick.id);
    }
  }

  const mixed = available.filter((a) => !usedIds.has(a.id)).slice(0, 9);
  return { gridArticles, mixed };
}

export const HomePage = () => {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [allArticles, setAllArticles] = useState([]);
  const [leadArticle, setLeadArticle] = useState(null);
  const [worldArticles, setWorldArticles] = useState([]);
  const [scienceArticles, setScienceArticles] = useState([]);
  const [people, setPeople] = useState([]);
  const [wonders, setWonders] = useState([]);
  const [poll, setPoll] = useState(null);
  const [meta, setMeta] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("today");
  const [loading, setLoading] = useState(true);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [mixedArticles, setMixedArticles] = useState([]); // Mixed feed from all categories
  const [newStoriesAvailable, setNewStoriesAvailable] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (user) {
      fetchBookmarks();
    }
  }, [user]);

  // ── Background polling: check every 10 min for new content ──
  useEffect(() => {
    let knownRefreshed = null;

    const checkForUpdates = async () => {
      try {
        const res = await axios.get(`${API}/meta`);
        const serverTime = res.data?.content_last_refreshed;
        if (!serverTime) return;
        if (knownRefreshed === null) {
          knownRefreshed = serverTime; // initialise on first check
        } else if (serverTime !== knownRefreshed) {
          setNewStoriesAvailable(true);
        }
      } catch (_) {
        // silent — polling failures shouldn't surface to kids
      }
    };

    // Start polling after a 10-minute delay so we don't hammer the server right after load
    const interval = setInterval(checkForUpdates, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setNewStoriesAvailable(false);
      setLoadError(false);

      // Fetch all data in parallel (seed now runs on backend startup)
      const [articlesRes, leadRes, peopleRes, wondersRes, pollRes, metaRes] = await Promise.all([
        axios.get(`${API}/articles`),
        axios.get(`${API}/articles/lead`),
        axios.get(`${API}/people`),
        axios.get(`${API}/wonders`),
        axios.get(`${API}/poll`),
        axios.get(`${API}/meta`)
      ]);

      setAllArticles(articlesRes.data);
      setArticles(articlesRes.data);
      setLeadArticle(leadRes.data);
      setPeople(peopleRes.data);
      setWonders(wondersRes.data);
      setPoll(pollRes.data);
      setMeta(metaRes.data);

      const { gridArticles, mixed } = buildHomepageSections(articlesRes.data, leadRes.data);
      setWorldArticles(gridArticles);
      setScienceArticles(articlesRes.data.filter(a => a.category === "science").slice(0, 3));
      setMixedArticles(mixed);

      // Deep-linked category (e.g. nav clicks from /story or /guidelines)
      const urlCategory = searchParams.get("category");
      if (urlCategory && ["world", "science", "tech", "sports"].includes(urlCategory)) {
        setActiveCategory(urlCategory);
        const diverse = articlesRes.data
          .filter(a => a.category === urlCategory)
          .slice(0, 9);
        if (urlCategory === "science") {
          setWorldArticles([]);
          setScienceArticles(diverse);
        } else {
          setWorldArticles(diverse);
          setScienceArticles([]);
        }
        setMixedArticles([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookmarks = async () => {
    try {
      const response = await axios.get(`${API}/bookmarks`, { withCredentials: true });
      const ids = new Set(response.data.map(a => a.id));
      setBookmarkedIds(ids);
    } catch (error) {
      // Not logged in or error
    }
  };

  const openArticle = useCallback((article) => {
    setSelectedArticle(article);
    setModalOpen(true);
  }, []);

  // Stable identity matters here: ArticleModal's focus-management effect
  // depends on onClose, so an inline closure re-ran the whole effect on every
  // HomePage render — tearing down the focus trap and bouncing focus back to
  // the card behind the modal mid-read.
  const closeModal = useCallback(() => {
    setModalOpen(false);
    setSelectedArticle(null);
  }, []);

  const handleVote = async (optionIndex) => {
    if (!poll) return;
    
    try {
      const response = await axios.post(`${API}/poll/vote`, {
        poll_id: poll.id,
        option_index: optionIndex
      });
      setPoll(response.data);
    } catch (error) {
      console.error("Error voting:", error);
    }
  };

  const handleCategoryChange = async (category) => {
    setActiveCategory(category);
    
    if (category === "today") {
      const { gridArticles, mixed } = buildHomepageSections(allArticles, leadArticle);
      setWorldArticles(gridArticles);
      setScienceArticles(allArticles.filter(a => a.category === "science").slice(0, 3));
      setMixedArticles(mixed);
    } else {
      // Category view with topic diversity
      const diverse = allArticles.filter(a => a.category === category).slice(0, 9);
      
      if (category === "science") {
        setWorldArticles([]);
        setScienceArticles(diverse);
      } else {
        setWorldArticles(diverse);
        setScienceArticles([]);
      }
      setMixedArticles([]);
    }
  };

  const handleBookmark = async (articleId) => {
    if (!user) return false;
    
    try {
      if (bookmarkedIds.has(articleId)) {
        await axios.delete(`${API}/bookmarks/${articleId}`, { withCredentials: true });
        setBookmarkedIds(prev => {
          const next = new Set(prev);
          next.delete(articleId);
          return next;
        });
        return false;
      } else {
        await axios.post(`${API}/bookmarks`, { article_id: articleId }, { withCredentials: true });
        setBookmarkedIds(prev => new Set([...prev, articleId]));
        return true;
      }
    } catch (error) {
      console.error("Bookmark error:", error);
      return bookmarkedIds.has(articleId);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-scoop-cream flex items-center justify-center">
        <div className="text-center">
          <div className="font-display text-3xl font-bold text-scoop-ink mb-2">
            The <em className="text-scoop-blue">Scoop</em>
          </div>
          <p className="text-scoop-muted text-sm">Loading today's stories...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-scoop-cream flex items-center justify-center px-6">
        <div className="text-center bg-white rounded-lg border border-scoop-border p-10 max-w-md">
          <span className="text-6xl block mb-4" aria-hidden="true">📡</span>
          <h1 className="font-display text-2xl font-bold text-scoop-ink mb-2">
            The news is taking a little break
          </h1>
          <p className="text-scoop-muted mb-6">
            We couldn't load today's stories. Check your connection and try again!
          </p>
          <button
            onClick={fetchData}
            className="bg-scoop-blue text-white text-sm font-bold px-8 py-3 rounded-full hover:bg-scoop-blue/90"
            data-testid="retry-load-btn"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const showWorld = activeCategory === "today" || activeCategory === "world";
  const showScience = activeCategory === "today" || activeCategory === "science";
  const showPeople = activeCategory === "today"; // People section shows on Today view only
  const showOtherCategory = activeCategory === "tech" || activeCategory === "sports";
  
  // Get category display info
  const getCategoryInfo = (cat) => {
    const info = {
      tech: { icon: "🤖", title: "Tech & Future" },
      sports: { icon: "⚽", title: "Sports" },
      world: { icon: "🌎", title: "Top Stories" },
      science: { icon: "🔬", title: "Science & Wow" },
    };
    return info[cat] || { icon: "📰", title: "News" };
  };

  return (
    <div className="scoop-page min-h-screen bg-scoop-cream" data-testid="home-page">
      <Masthead 
        lastUpdated={meta?.content_last_refreshed} 
        activeCategory={activeCategory}
        onCategoryChange={handleCategoryChange}
      />
      
      <WeatherBar />
      
      <div className="page-grid max-w-[1200px] mx-auto px-5 md:px-10 py-8 md:py-11 grid grid-cols-1 lg:grid-cols-[1fr_308px] gap-8 lg:gap-14">
        {/* Main Content */}
        <main>
          <h1 className="sr-only">The Scoop — today's news for curious kids</h1>
          <SafetyRibbon />

          {/* New Stories Banner */}
          {newStoriesAvailable && (
            <button
              onClick={fetchData}
              className="w-full mb-6 py-2.5 px-4 bg-scoop-blue text-white text-sm font-semibold rounded-full flex items-center justify-center gap-2 hover:bg-scoop-blue/90 transition-colors animate-fadeIn"
            >
              ✨ New stories are ready — tap to refresh
            </button>
          )}

          {/* World News Section */}
          {showWorld && (
            <>
              <SectionLabel 
                icon="🌎" 
                title="Top Stories" 
                showLive={true} 
              />
              
              {leadArticle && activeCategory === "today" && (
                <LeadStory 
                  article={leadArticle} 
                  onClick={() => openArticle(leadArticle)} 
                />
              )}
              
              {worldArticles.length > 0 && (
                <div className="story-grid-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-11 pb-11 border-b border-scoop-border">
                  {worldArticles.map((article, index) => (
                    <StoryCard 
                      key={article.id} 
                      article={article} 
                      onClick={() => openArticle(article)}
                      delay={index + 1}
                      isBookmarked={bookmarkedIds.has(article.id)}
                      onBookmark={user ? () => handleBookmark(article.id) : null}
                    />
                  ))}
                </div>
              )}
            </>
          )}
          
          {/* Tech, Sports, Health - New Categories */}
          {showOtherCategory && worldArticles.length > 0 && (
            <>
              <SectionLabel 
                icon={getCategoryInfo(activeCategory).icon} 
                title={getCategoryInfo(activeCategory).title} 
              />
              
              <div className="story-grid-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-11 pb-11 border-b border-scoop-border">
                {worldArticles.map((article, index) => (
                  <StoryCard 
                    key={article.id} 
                    article={article} 
                    onClick={() => openArticle(article)}
                    delay={index + 1}
                    isBookmarked={bookmarkedIds.has(article.id)}
                    onBookmark={user ? () => handleBookmark(article.id) : null}
                  />
                ))}
              </div>
            </>
          )}
          
          {/* Science Section - shown only when filtering by science */}
          {activeCategory === "science" && scienceArticles.length > 0 && (
            <>
              <SectionLabel icon="🔬" title="Science & Wow" />
              
              <div className="story-grid-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-11 pb-11 border-b border-scoop-border">
                {scienceArticles.map((article, index) => (
                  <StoryCard 
                    key={article.id} 
                    article={article} 
                    onClick={() => openArticle(article)}
                    delay={index + 1}
                    isBookmarked={bookmarkedIds.has(article.id)}
                    onBookmark={user ? () => handleBookmark(article.id) : null}
                  />
                ))}
              </div>
            </>
          )}
          
          {/* Mixed Stories Section - shows variety from all categories on Today view */}
          {activeCategory === "today" && mixedArticles.length > 0 && (
            <>
              <SectionLabel icon="📰" title="More Stories" />
              
              <div className="story-grid-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-11 pb-11 border-b border-scoop-border">
                {mixedArticles.map((article, index) => (
                  <StoryCard 
                    key={article.id} 
                    article={article} 
                    onClick={() => openArticle(article)}
                    delay={index + 1}
                    isBookmarked={bookmarkedIds.has(article.id)}
                    onBookmark={user ? () => handleBookmark(article.id) : null}
                  />
                ))}
              </div>
            </>
          )}
          
          {/* People Section */}
          {showPeople && (
            <>
              <SectionLabel icon="🏅" title="People Making a Difference" />
              
              <div className="people-grid grid grid-cols-1 md:grid-cols-2 gap-4 mb-11">
                {people.slice(0, 4).map((person, index) => (
                  <PersonCard 
                    key={person.id} 
                    person={person} 
                    onClick={() => openArticle({
                      ...person,
                      headline: person.title,
                      deck: person.description,
                      category: "people",
                      sub_category: "People"
                    })}
                    delay={index + 1}
                  />
                ))}
              </div>
            </>
          )}
        </main>
        
        {/* Sidebar */}
        <aside className="space-y-9 lg:sticky lg:top-6 lg:self-start">
          <div id="poll-section">
            {poll && (
              <Poll 
                poll={poll} 
                onVote={handleVote} 
              />
            )}
          </div>
          
          <QuickWonders 
            wonders={wonders} 
            onWonderClick={(wonder) => openArticle({
              ...wonder,
              headline: wonder.title,
              deck: wonder.description,
              category: "wonder",
              sub_category: "Nature",
              emoji: "✨",
              color: "#3A9E72"
            })}
          />
          
          <TrustBox />
        </aside>
      </div>
      
      <Footer lastUpdated={meta?.content_last_refreshed} />
      
      <ArticleModal 
        article={selectedArticle} 
        isOpen={modalOpen} 
        onClose={closeModal}
        isBookmarked={selectedArticle ? bookmarkedIds.has(selectedArticle.id) : false}
        onBookmark={user && selectedArticle ? () => handleBookmark(selectedArticle.id) : null}
      />
    </div>
  );
};

export default HomePage;
